import { useEffect, useMemo } from 'react';

import {
  ApprovalDecision,
  InboxEntry,
  InboxEntryViewFields,
  WebSocketEntityType,
} from '@this+that/models';
import log from 'loglevel';

import { CardStatusFilter } from '../../../components/DoBoxFilters/types';
import { useFetchInboxEntries } from '../../../hooks/InboxEntries/useFetchInboxEntries';
import {
  buildDoBoxBackendFilters,
  buildDoBoxBackendSort,
} from '../../../pages/private/DoBox/utils/buildDoBoxBackendQuery';
import { buildDoBoxRefreshBucketLimits } from '../../../pages/private/DoBox/utils/buildDoBoxRefreshBucketLimits';
import { matchesDoBoxAttributeFilters } from '../../../pages/private/DoBox/utils/matchesDoBoxViewFilters';
import { resolveViewBucketKeyForEntry } from '../../../pages/private/DoBox/utils/resolveBucketKeyForEntry';
import {
  selectHasActiveFilters,
  useDoBoxFilterSortStore,
  useFetchCacheStore,
  useInboxEntriesStore,
  usePendingActionsStore,
} from '../../../store';
import { getCurrentDoBoxViewFilters } from '../../../store/Inbox/InboxEntries/handlers';
import { indexedDBAdapter } from '../../../store/utils/indexedDBAdapter';
import { shouldSuppressByTombstone } from '../../../store/utils/tombstones';
import { DOBOX_PAGE_LIMIT } from '../../../utils/constants';
import { consumeDiscount } from '../../../utils/inboxEntryDiscountLedger';
import { getOptimisticMove } from '../../../utils/optimisticMoveGuard';
import {
  useWebSocket,
  WebSocketEventType,
} from '../../WebSocket/WebSocketContext';

// Fields that change on every write regardless of user-visible content, so they
// must be ignored when deciding whether an echo is a redundant no-op.
const VOLATILE_ECHO_FIELDS = new Set([
  'updatedAt',
  'version',
  'communications',
]);

// A reorder persists one write per touched entry, so the server streams back one
// MODIFY echo each. When the (optimistically-overlaid) echo already matches every
// user-visible field of the entry we hold, re-applying it only churns re-renders.
// Comparing every field the echo carries keeps a genuine concurrent edit (subject,
// checklist, labels, …) from being dropped — that still differs, so it's applied.
const isRedundantEcho = (
  itemToApply: InboxEntry,
  existingEntry: InboxEntry,
): boolean => {
  const applied = itemToApply as unknown as Record<string, unknown>;
  const existing = existingEntry as unknown as Record<string, unknown>;
  for (const key of Object.keys(applied)) {
    if (VOLATILE_ECHO_FIELDS.has(key)) {
      continue;
    }
    if (JSON.stringify(applied[key]) !== JSON.stringify(existing[key])) {
      return false;
    }
  }
  return true;
};

export { shouldSuppressByTombstone } from '../../../store/utils/tombstones';

// indexedDBAdapter.markAsDeleted swallows and logs its own errors, so callers
// don't need to.
const markInboxEntryTombstone = (
  id: string,
  version: number | null | undefined,
) => indexedDBAdapter.markAsDeleted(id, 'inbox-entry', version ?? null);

// A removal of an entry never paginated into the active list decremented the
// server's total — classify it from the WS payload's own fields, since there
// is no local copy to diff against.
const resolveUnloadedItemBucketKey = (item: InboxEntry) =>
  resolveViewBucketKeyForEntry(item, getCurrentDoBoxViewFilters());

// A tombstone at or past the item's version hides it; an older one is stale
// and is cleared.
const passesTombstone = async (item: InboxEntry): Promise<boolean> => {
  const deletedItemRecord = await indexedDBAdapter.getDeletedItem(item.id);
  if (shouldSuppressByTombstone(deletedItemRecord, item.version)) {
    return false;
  }
  if (deletedItemRecord) {
    await indexedDBAdapter.unmarkAsDeleted(item.id);
  }
  return true;
};

const isOlderThanHeld = (
  item: InboxEntry,
  existingEntry: InboxEntry | undefined,
): boolean =>
  item.version != null &&
  existingEntry?.version != null &&
  item.version <= existingEntry.version;

// Under "All" the view showed the entry while it was resolved, so the server's
// total already holds it. A suggestion follows the server's rule, not the
// client's always-visible one: the server filters resolvedAt in every bucket,
// so a resolved suggestion is out of the unresolved view and nowhere else.
const entersViewOnReopen = (item: InboxEntry): boolean => {
  const viewFilters = getCurrentDoBoxViewFilters();
  if (item.isSuggestion === true) {
    return (
      viewFilters.selectedCardStatusFilter === CardStatusFilter.UNRESOLVED &&
      matchesDoBoxAttributeFilters(item, viewFilters)
    );
  }
  const whileResolved = { ...item, resolvedAt: item.updatedAt || Date.now() };
  return (
    resolveViewBucketKeyForEntry(item, viewFilters) !== null &&
    resolveViewBucketKeyForEntry(whileResolved, viewFilters) === null
  );
};

const VIEW_FIELD_KEYS = [
  'taskSubgroupId',
  'entryKind',
  'isSuggestion',
  'resolvedAt',
  'approvalDecision',
  'taskLabelIds',
  'participantIds',
  'priority',
  'dueAt',
] as const satisfies ReadonlyArray<keyof InboxEntryViewFields>;

// A key missing from `previous` was absent before, so it must not fall back to the item's value.
const rebuildPreviousEntry = (
  item: InboxEntry,
  previous: InboxEntryViewFields,
): InboxEntry => {
  const previousEntry: InboxEntry = { ...item };
  for (const key of VIEW_FIELD_KEYS) {
    (previousEntry as unknown as Record<string, unknown>)[key] = previous[key];
  }
  return previousEntry;
};

const SUGGESTION_APPROVAL_DECISION_BY_FILTER: Partial<
  Record<CardStatusFilter, ApprovalDecision>
> = {
  [CardStatusFilter.APPROVED]: ApprovalDecision.APPROVED,
  [CardStatusFilter.REJECTED]: ApprovalDecision.REJECTED,
};

const matchesServerCardStatus = (
  entry: InboxEntry,
  cardStatusFilter: CardStatusFilter,
): boolean => {
  const isResolved = entry.resolvedAt != null;
  if (cardStatusFilter === CardStatusFilter.ALL) {
    return true;
  }
  if (cardStatusFilter === CardStatusFilter.RESOLVED) {
    return isResolved;
  }
  const decision = SUGGESTION_APPROVAL_DECISION_BY_FILTER[cardStatusFilter];
  if (decision) {
    return isResolved && entry.approvalDecision === decision;
  }
  return !isResolved;
};

// The client treats a suggestion as always visible; the server applies the card-status filter to it like any entry.
const resolveServerBucketKey = (
  entry: InboxEntry,
  viewFilters: ReturnType<typeof getCurrentDoBoxViewFilters>,
) =>
  entry.isSuggestion === true &&
  !matchesServerCardStatus(entry, viewFilters.selectedCardStatusFilter)
    ? null
    : resolveViewBucketKeyForEntry(entry, viewFilters);

export function useInboxEntriesSync() {
  const { subscribe } = useWebSocket();
  const saveDetailedInboxEntry = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.saveDetailedInboxEntry,
  );
  const updateInboxEntry = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.updateInboxEntry,
  );
  const removeInboxEntry = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.removeInboxEntry,
  );
  const removeInboxEntryFromActiveList = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.removeInboxEntryFromActiveList,
  );
  const setModalInboxEntry = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.setModalInboxEntry,
  );
  const adjustBucketTotal = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.adjustBucketTotal,
  );
  const taskGroupId = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.taskGroupId,
  );
  const clearCache = useFetchCacheStore((s) => s.clearCache);

  const hasActiveFilters = useDoBoxFilterSortStore(selectHasActiveFilters);
  const selectedFilterLabelIds = useDoBoxFilterSortStore(
    (s) => s.selectedFilterLabelIds,
  );
  const showNoLabelsFilter = useDoBoxFilterSortStore(
    (s) => s.showNoLabelsFilter,
  );
  const selectedDueDateFilters = useDoBoxFilterSortStore(
    (s) => s.selectedDueDateFilters,
  );
  const selectedPriorityFilters = useDoBoxFilterSortStore(
    (s) => s.selectedPriorityFilters,
  );
  const selectedCardStatusFilter = useDoBoxFilterSortStore(
    (s) => s.selectedCardStatusFilter,
  );
  const showNoMembersFilter = useDoBoxFilterSortStore(
    (s) => s.showNoMembersFilter,
  );
  const selectedFilterMemberIds = useDoBoxFilterSortStore(
    (s) => s.selectedFilterMemberIds,
  );
  const unreadOnly = useDoBoxFilterSortStore((s) => s.unreadOnly);
  const sortField = useDoBoxFilterSortStore((s) => s.sortField);
  const sortOrder = useDoBoxFilterSortStore((s) => s.sortOrder);

  const syncFetchFilters = useMemo(
    () =>
      buildDoBoxBackendFilters({
        hasActiveFilters,
        selectedFilterLabelIds,
        showNoLabelsFilter,
        selectedDueDateFilters,
        selectedPriorityFilters,
        selectedCardStatusFilter,
        showNoMembersFilter,
        selectedFilterMemberIds,
        unreadOnly,
      }),
    [
      hasActiveFilters,
      selectedFilterLabelIds,
      showNoLabelsFilter,
      selectedDueDateFilters,
      selectedPriorityFilters,
      selectedCardStatusFilter,
      showNoMembersFilter,
      selectedFilterMemberIds,
      unreadOnly,
    ],
  );

  const syncFetchSort = useMemo(
    () => buildDoBoxBackendSort({ sortField, sortOrder }),
    [sortField, sortOrder],
  );

  const { handleFetchInitial } = useFetchInboxEntries({
    taskGroupId,
    limit: DOBOX_PAGE_LIMIT,
    filters: syncFetchFilters,
    sort: syncFetchSort,
  });

  useEffect(() => {
    const unsubscribeReconnect = subscribe(
      WebSocketEntityType.InboxEntry,
      WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
      async () => {
        log.info(
          '[InboxEntriesSync] Reconnected after long pause, clearing cache and fetching entries',
        );
        clearCache();
        await indexedDBAdapter.cleanupDeletedItems();
        const activeTaskGroupState =
          useInboxEntriesStore.getState().taskManager.activeTaskGroup;
        const areSubgroupsReady =
          activeTaskGroupState.taskSubgroupsFetchedForTaskGroupId ===
          activeTaskGroupState.taskGroupId;
        if (!areSubgroupsReady) {
          // Subgroups for the active group haven't loaded yet (e.g. reconnect
          // races the initial useFetchTaskSubgroups load), so taskSubgroups is
          // an unreliable empty/stale snapshot here. Refetching with it would
          // wholesale-replace inboxEntries/bucketPagination with only the
          // backlog bucket, wiping every subgroup-assigned card. Skip and let
          // the board's own initial-load effects repopulate once ready.
          log.info(
            '[InboxEntriesSync] Subgroups not loaded yet for the active group, skipping bucket refetch on reconnect',
          );
          return;
        }
        const subgroupIds = activeTaskGroupState.taskSubgroups.map(
          (taskSubgroup) => taskSubgroup.id,
        );
        // The old flat fetch overwrote inboxEntries with a single flat page
        // while bucketPagination cursors kept pointing at buckets that page
        // no longer covered, corrupting per-bucket state. Refetching each
        // bucket's first page keeps per-bucket state consistent, and
        // bucketLimits preserves each bucket's already-loaded depth instead
        // of truncating it back down to the first page.
        const bucketLimits = buildDoBoxRefreshBucketLimits({
          inboxEntries: activeTaskGroupState.inboxEntries,
          knownSubgroupIds: subgroupIds,
        });
        await handleFetchInitial({
          subgroupIds,
          limitPerBucket: DOBOX_PAGE_LIMIT,
          bucketLimits,
        });
      },
    );

    // The server's event is the confirmation of a removal, so the record a
    // handler leaves for a local removal must not outlive it.
    const removeConfirmedByServer = (id: string, remove: () => void) => {
      remove();
      consumeDiscount(id);
    };

    const applyDeletionOfUnloaded = async (
      item: InboxEntry,
      alreadyDeleted: boolean,
      discountedLocally: boolean,
    ) => {
      const { taskGroupId, modalInboxEntry } =
        useInboxEntriesStore.getState().taskManager.activeTaskGroup;
      if (
        item.taskGroupId === taskGroupId &&
        !alreadyDeleted &&
        !discountedLocally
      ) {
        adjustBucketTotal(resolveUnloadedItemBucketKey(item), -1);
      }
      if (modalInboxEntry?.id === item.id) {
        setModalInboxEntry({ ...modalInboxEntry, ...item });
      }
    };

    // The deleted entry stays in the details modal's slot when the open modal
    // pinned it there: dropped outright, the modal went blank, refetched the
    // row and drew it again with its actions, as if nothing had happened. Any
    // other slot is left alone, so a background delete never takes it.
    const applyDeletion = async (item: InboxEntry, alreadyDeleted = false) => {
      const { inboxEntries, modalInboxEntry } =
        useInboxEntriesStore.getState().taskManager.activeTaskGroup;
      const existingEntry = inboxEntries.find((entry) => entry.id === item.id);
      const discountedLocally = consumeDiscount(item.id);
      if (existingEntry && modalInboxEntry?.id === item.id) {
        removeConfirmedByServer(item.id, () =>
          removeInboxEntryFromActiveList({ ...existingEntry, ...item }),
        );
      } else if (existingEntry) {
        removeConfirmedByServer(item.id, () => removeInboxEntry(item.id));
      } else {
        await applyDeletionOfUnloaded(item, alreadyDeleted, discountedLocally);
      }
      await markInboxEntryTombstone(item.id, item.version);
    };

    const unsubscribeInsert = subscribe<InboxEntry>(
      WebSocketEntityType.InboxEntry,
      WebSocketEventType.INSERT,
      async ({ item }) => {
        if (!item) {
          return;
        }

        if (!(await passesTombstone(item))) {
          return;
        }

        if (item.deletedAt != null) {
          await applyDeletion(item);
          return;
        }

        const pendingAction = usePendingActionsStore
          .getState()
          .getPendingActionForEntry(item.id);
        if (pendingAction) {
          saveDetailedInboxEntry(item);
          return;
        }

        const state = useInboxEntriesStore.getState();
        const activeTaskGroupId = state.taskManager.activeTaskGroup.taskGroupId;
        const alreadyInState =
          state.taskManager.activeTaskGroup.inboxEntries.some(
            (entry) => entry.id === item.id,
          );

        if (alreadyInState) {
          return;
        }

        if (item.taskGroupId === activeTaskGroupId) {
          saveDetailedInboxEntry(item);
        }
      },
    );

    // With the old values the entry's move into or out of the view is known exactly.
    const applyViewTransitionOfUnloaded = async (
      item: InboxEntry,
      itemToApply: InboxEntry,
      previous: InboxEntryViewFields,
    ) => {
      const { taskGroupId } =
        useInboxEntriesStore.getState().taskManager.activeTaskGroup;
      if (item.taskGroupId !== taskGroupId) {
        return;
      }
      const viewFilters = getCurrentDoBoxViewFilters();
      const before = resolveServerBucketKey(
        rebuildPreviousEntry(item, previous),
        viewFilters,
      );
      const after = resolveServerBucketKey(itemToApply, viewFilters);
      if (before === after || !(await passesTombstone(item))) {
        return;
      }
      if (before !== null && !consumeDiscount(item.id)) {
        adjustBucketTotal(before, -1);
      }
      if (after !== null) {
        saveDetailedInboxEntry(itemToApply);
      }
    };

    // Without the old values, any other MODIFY of an unloaded entry is already in the
    // server's total; a reopen the view hid while resolved is the one that is new to it.
    // Without the old values, any other MODIFY of an unloaded entry is already in the
    // server's total; a reopen the view hid while resolved is the one that is new to it.
    const applyReopenOfUnloaded = async (
      item: InboxEntry,
      itemToApply: InboxEntry,
    ) => {
      const { taskGroupId } =
        useInboxEntriesStore.getState().taskManager.activeTaskGroup;
      if (
        item.taskGroupId === taskGroupId &&
        entersViewOnReopen(item) &&
        (await passesTombstone(item))
      ) {
        saveDetailedInboxEntry(itemToApply);
      }
    };

    const unsubscribeModify = subscribe<InboxEntry>(
      WebSocketEntityType.InboxEntry,
      WebSocketEventType.MODIFY,
      async ({ item, reopened, previous, alreadyDeleted }) => {
        if (!item) {
          return;
        }
        if (item.deletedAt != null) {
          await applyDeletion(item, alreadyDeleted === true);
          return;
        }

        const {
          taskGroupId: activeTaskGroupId,
          inboxEntries,
          modalInboxEntry,
        } = useInboxEntriesStore.getState().taskManager.activeTaskGroup;
        const existingEntry = inboxEntries.find(
          (entry) => entry.id === item.id,
        );
        // An entry open in the details modal can live only in the modal slot (deep link,
        // search hit, linked task) — it is not in the paginated list, but it is on screen.
        const modalOnlyEntry =
          !existingEntry && modalInboxEntry?.id === item.id
            ? modalInboxEntry
            : undefined;
        if (isOlderThanHeld(item, existingEntry ?? modalOnlyEntry)) {
          return;
        }

        const optimisticMove = getOptimisticMove(item.id);
        const itemToApply = optimisticMove
          ? {
              ...item,
              taskSubgroupId: optimisticMove.taskSubgroupId,
              orderKey: optimisticMove.orderKey,
            }
          : item;

        // Skip re-applying our own just-persisted reorder: while an optimistic
        // move is still guarded, an echo that changes nothing we render is pure
        // churn (one MODIFY per reordered entry).
        if (
          optimisticMove &&
          existingEntry &&
          isRedundantEcho(itemToApply, existingEntry)
        ) {
          return;
        }

        const pendingAction = usePendingActionsStore
          .getState()
          .getPendingActionForEntry(item.id);
        if (pendingAction) {
          updateInboxEntry(itemToApply);
          return;
        }

        if (modalOnlyEntry && item.taskGroupId === activeTaskGroupId) {
          updateInboxEntry(itemToApply);
          return;
        }

        if (!existingEntry) {
          if (previous) {
            await applyViewTransitionOfUnloaded(item, itemToApply, previous);
          } else if (reopened) {
            await applyReopenOfUnloaded(item, itemToApply);
          }
          return;
        }
        if (item.taskGroupId === activeTaskGroupId) {
          saveDetailedInboxEntry(itemToApply);
        } else {
          removeConfirmedByServer(item.id, () => removeInboxEntry(item.id));
        }
      },
    );

    const unsubscribeRemove = subscribe<InboxEntry>(
      WebSocketEntityType.InboxEntry,
      WebSocketEventType.REMOVE,
      async ({ item }) => {
        if (!item) {
          return;
        }

        const state = useInboxEntriesStore.getState();
        const activeTaskGroupId = state.taskManager.activeTaskGroup.taskGroupId;

        // A REMOVE from another partition is the delete-leg echo of a MOVE —
        // it must never touch the active list, or it deletes the task from
        // the destination group's view.
        if (item.taskGroupId !== activeTaskGroupId) {
          return;
        }

        const discountedLocally = consumeDiscount(item.id);
        const existingEntry =
          state.taskManager.activeTaskGroup.inboxEntries.find(
            (entry) => entry.id === item.id,
          );

        // State already holds a newer copy (e.g. the moved-in row when the
        // paired INSERT arrived first) — this REMOVE is stale, ignore it.
        if (
          existingEntry?.version != null &&
          item.version != null &&
          existingEntry.version > item.version
        ) {
          return;
        }

        const pendingAction = usePendingActionsStore
          .getState()
          .getPendingActionForEntry(item.id);
        if (pendingAction) {
          usePendingActionsStore
            .getState()
            .removePendingAction(pendingAction.id);
        }

        if (existingEntry) {
          removeConfirmedByServer(item.id, () => removeInboxEntry(item.id));
        } else if (item.deletedAt == null && !discountedLocally) {
          // A soft-deleted row was counted out on its deletedAt MODIFY; this REMOVE is the TTL purge.
          adjustBucketTotal(resolveUnloadedItemBucketKey(item), -1);
        }

        // REMOVE on the active partition is a hard delete: tombstone it so a
        // re-delivered older INSERT/MODIFY stays suppressed. A move's paired
        // INSERT carries version+1, so it passes the version check above.
        await markInboxEntryTombstone(item.id, item.version);
      },
    );

    return () => {
      unsubscribeReconnect();
      unsubscribeInsert();
      unsubscribeModify();
      unsubscribeRemove();
    };
  }, [
    subscribe,
    handleFetchInitial,
    saveDetailedInboxEntry,
    updateInboxEntry,
    removeInboxEntry,
    removeInboxEntryFromActiveList,
    setModalInboxEntry,
    adjustBucketTotal,
  ]);
}
